/**
 * Search History Service
 * Handles all operations related to user search history
 */

import { apiRequest } from './api';
import config from '../config';

// Standardized search data structure for consistency
const createStandardSearchData = (
  address,
  validatedAddress,
  flatNumber,
  searchType,
  apiResponseData,
  propertyType = '',
  aiResponses = null,
) => ({
  address,
  postcode: validatedAddress?.postcode || '',
  flat_number: flatNumber || '',
  search_type: searchType,
  lat: validatedAddress?.lat || 0,
  lng: validatedAddress?.lng || 0,
  timestamp: new Date().toISOString(),
  api_response_data: apiResponseData || null,
  street_number: validatedAddress?.street_number || '',
  street: validatedAddress?.street || '',
  property_type: propertyType,
  ai_responses: aiResponses,
});

/**
 * Get user's search history from backend
 *
 * @returns {Promise<Array>} - List of search history items
 */
const getSearchHistory = async () => {
  const token = localStorage.getItem('token');
  return apiRequest(
    `${config.apiUrl}/search_history/get_search_history`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
    'search_history',
  );
};

/**
 * Save a new search to history or update an existing one
 *
 * @param {Object} searchData - Search data to save
 * @returns {Promise<Object>} - API response with search ID
 */
const saveSearch = async (searchData) => {
  const token = localStorage.getItem('token');
  const isUpdate = searchData.id && !String(searchData.id).startsWith('temp-');

  console.log(`${isUpdate ? 'Updating' : 'Saving new'} search to database:`, searchData);
  console.log('Using token:', token ? 'Token exists' : 'No token available');

  // Log specific fields we're having issues with
  console.log('property_type:', searchData.property_type);
  console.log('ai_responses:', searchData.ai_responses);
  console.log('id:', searchData.id);

  // Ensure property_type and ai_responses are properly set
  const enhancedSearchData = {
    ...searchData,
    property_type: searchData.property_type || 'buy',
    ai_responses: searchData.ai_responses || null,
  };

  // If no token is available, don't attempt to save search history
  if (!token) {
    console.log('No authentication token available, skipping search history save');
    return { success: false, message: 'Not authenticated' };
  }

  try {
    // Use the appropriate endpoint based on whether this is an update or a new search
    const endpoint = isUpdate
      ? `${config.apiUrl}/search_history/update_search/${searchData.id}`
      : `${config.apiUrl}/search_history/save_search`;

    console.log(`Using endpoint: ${endpoint}`);

    const response = await apiRequest(
      endpoint,
      {
        method: 'POST',
        body: JSON.stringify(enhancedSearchData),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      null, // Don't cache this request
    );
    console.log(`Search ${isUpdate ? 'updated' : 'saved'} successfully:`, response);
    return response;
  } catch (error) {
    console.error(`Error ${isUpdate ? 'updating' : 'saving'} search to database:`, error);
    // Return a non-throwing error object so the UI doesn't break
    return {
      success: false,
      error: error.message || `Failed to ${isUpdate ? 'update' : 'save'} search`,
    };
  }
};

/**
 * Update an existing search
 *
 * @param {string|number} searchId - ID of the search to update
 * @param {Object} searchData - Updated search data
 * @returns {Promise<Object>} - API response
 */
const updateSearch = async (searchId, searchData) => {
  const token = localStorage.getItem('token');
  return apiRequest(`${config.apiUrl}/search_history/update_search/${searchId}`, {
    method: 'PUT',
    body: JSON.stringify(searchData),
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

/**
 * Delete a search from history
 *
 * @param {string|number} searchId - ID of the search to delete
 * @returns {Promise<Object>} - API response
 */
const deleteSearch = async (searchId) => {
  const token = localStorage.getItem('token');
  return apiRequest(`${config.apiUrl}/search_history/searches/${searchId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

/**
 * Get a specific search from history by ID
 *
 * @param {string|number} searchId - ID of the search to retrieve
 * @returns {Promise<Object>} - Search data object
 */
const getSearchById = async (searchId) => {
  const token = localStorage.getItem('token');
  if (!token || !searchId) {
    console.log('No token or search ID available, cannot retrieve search');
    return null;
  }

  try {
    const response = await apiRequest(`${config.apiUrl}/search_history/get_search/${searchId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Retrieved search by ID:', response);

    // Handle apiData field - parse it if it's a string
    if (response && response.api_data && typeof response.api_data === 'string') {
      try {
        response.apiData = JSON.parse(response.api_data);
        console.log('Successfully parsed API data from string');
      } catch (parseError) {
        console.error('Error parsing API data:', parseError);
        response.apiData = null;
      }
    } else if (response && response.api_data) {
      // If it's already an object, just assign it
      response.apiData = response.api_data;
    }

    return response;
  } catch (error) {
    console.error(`Error retrieving search with ID ${searchId}:`, error);
    return null;
  }
};

/**
 * Synchronize local and backend search history
 *
 * @param {Array} localSearches - Local search history
 * @returns {Promise<Array>} - Merged and deduplicated search history
 */
const syncSearchHistory = async (localSearches = []) => {
  try {
    // Get backend searches
    const backendSearches = await getSearchHistory();

    // Merge local and backend searches, removing duplicates
    const mergedSearches = [...backendSearches];

    // Add local searches that don't exist in backend
    localSearches.forEach((localSearch) => {
      if (
        !backendSearches.some(
          (bs) => bs.address === localSearch.address && bs.flat_number === localSearch.flat_number,
        )
      ) {
        mergedSearches.push(localSearch);
      }
    });

    // Sort by timestamp (newest first)
    return mergedSearches.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  } catch (error) {
    console.error('Error syncing search history:', error);
    // If sync fails, return local searches
    return localSearches;
  }
};

/**
 * Handle local storage of search history
 *
 * @param {Object} searchData - Search data to store
 * @param {boolean} isPremium - Whether user has premium subscription
 * @param {number} limit - Maximum number of searches to keep
 * @returns {Array} - Updated search history
 */
const updateLocalSearchHistory = (searchData, isPremium, limit = isPremium ? 100 : 6) => {
  // Get current local searches
  let localSearches = JSON.parse(localStorage.getItem('previousSearches') || '[]');

  // Check for duplicate search
  const existingIndex = localSearches.findIndex(
    (search) =>
      search.address === searchData.address && search.flat_number === searchData.flat_number,
  );

  if (existingIndex >= 0) {
    // Update existing search
    localSearches[existingIndex] = {
      ...localSearches[existingIndex],
      ...searchData,
    };
  } else {
    // Add new search at the beginning
    localSearches = [searchData, ...localSearches];
  }

  // Apply limit
  localSearches = localSearches.slice(0, limit);

  // Save to local storage
  localStorage.setItem('previousSearches', JSON.stringify(localSearches));

  return localSearches;
};

export {
  getSearchHistory,
  saveSearch,
  updateSearch,
  deleteSearch,
  syncSearchHistory,
  updateLocalSearchHistory,
  createStandardSearchData,
  getSearchById,
};
